import { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Create a context for authentication
const AuthContext = createContext();

// Provider component
export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [userInfoLoaded, setUserInfoLoaded] = useState(false);

    useEffect(() => {
        // Check if user is logged in and load user details
        const loadUserFromToken = async () => {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            
            if (token) {
                try {
                    // Set token for all axios requests
                    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                    
                    // Get user details from API
                    const response = await axios.get('https://api.emboxvn.com/auth/me');
                    const userInfo = response.data.data;
                    
                    setUser(userInfo);
                    setIsAuthenticated(true);
                    setUserInfoLoaded(true);
                } catch (error) {
                    console.error("Error loading user info:", error);
                    localStorage.removeItem('authToken');
                    delete axios.defaults.headers.common['Authorization'];
                    setIsAuthenticated(false);
                    setUser(null);
                }
            }
            
            setLoading(false);
        };

        loadUserFromToken();
    }, []);

    const login = async (token) => {
        localStorage.setItem('authToken', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        try {
            // Get detailed user info from API
            const response = await axios.get('https://api.emboxvn.com/auth/me');
            const userInfo = response.data.data;
            
            setUser(userInfo);
            setIsAuthenticated(true);
            setUserInfoLoaded(true);
            return userInfo;
        } catch (error) {
            console.error("Error getting user info after login:", error);
            throw error;
        }
    };

    const logout = () => {
        // Attempt to call logout API (no need to await)
        try {
            axios.post('https://api.emboxvn.com/auth/logout');
        } catch (error) {
            console.error("Error during logout:", error);
        }

        // Clear local storage and state regardless of API response
        localStorage.removeItem('authToken');
        localStorage.removeItem('username');
        delete axios.defaults.headers.common['Authorization'];
        setIsAuthenticated(false);
        setUser(null);
        setUserInfoLoaded(false);
    };

    // Reload user data from API (useful if user data might change)
    const refreshUserInfo = async () => {
        if (!isAuthenticated) return null;
        
        try {
            const response = await axios.get('https://api.emboxvn.com/auth/me');
            const userInfo = response.data.data;
            setUser(userInfo);
            setUserInfoLoaded(true);
            return userInfo;
        } catch (error) {
            console.error("Error refreshing user info:", error);
            return null;
        }
    };

    return (
        <AuthContext.Provider value={{ 
            isAuthenticated, 
            user, 
            login, 
            logout, 
            loading,
            userInfoLoaded,
            refreshUserInfo
        }}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use the auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// HOC to protect routes
export const withAuth = (Component) => {
    const AuthProtected = (props) => {
        const { isAuthenticated, loading } = useAuth();
        const navigate = useNavigate();

        useEffect(() => {
            if (!loading && !isAuthenticated) {
                navigate('/login', { state: { from: window.location.pathname } });
            }
        }, [isAuthenticated, loading, navigate]);

        if (loading) {
            return <div>Loading...</div>;
        }

        return isAuthenticated ? <Component {...props} /> : null;
    };

    return AuthProtected;
};

export default useAuth; 